import { format } from 'date-fns';
import { buildXlsx, CellValue } from './xlsx';

export type ExportFormat = 'csv' | 'xlsx';

export interface ExportTable {
  headers: string[];
  rows: CellValue[][];
}

/**
 * Turns a list of per-record field maps into a rectangular table.
 *
 * Records do not have to share the same keys - polymorphic entities such as Klass add
 * fields specific to their subtype - so the header set is the union of every record's
 * keys, in the order they are first seen. Records missing a column export as blank.
 */
export const toTable = (records: Map<string, CellValue>[]): ExportTable => {
  const headers: string[] = [];
  const seen = new Set<string>();

  for (const record of records) {
    for (const key of record.keys()) {
      if (seen.has(key)) continue;
      seen.add(key);
      headers.push(key);
    }
  }

  const rows = records.map(record => headers.map(header => record.get(header) ?? null));
  return { headers, rows };
};

/** A leading =, +, -, @, tab or CR makes a spreadsheet read the cell as a formula. */
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;
const NEEDS_QUOTING = /[",\r\n]/;

const csvCell = (value: CellValue): string => {
  if (value === null || value === undefined) return '';
  let text = String(value);

  // Numbers are exported as numbers, so a *string* that opens with one of the trigger
  // characters is free text we have to neutralise. Anything that parses as a number is
  // left alone, otherwise every negative value written as text would gain a quote.
  if (typeof value === 'string' && FORMULA_TRIGGER.test(text) && !Number.isFinite(Number(text))) {
    text = `'${text}`;
  }

  if (NEEDS_QUOTING.test(text)) {
    text = `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

/** RFC 4180 CSV: CRLF line endings, quotes doubled inside quoted fields. */
export const buildCsv = ({ headers, rows }: ExportTable): string =>
  [headers as CellValue[], ...rows].map(row => row.map(csvCell).join(',')).join('\r\n');

export const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Writes the records to the chosen format and hands the file to the browser.
 *
 * @param records     one field map per row, as produced by the model's export helper
 * @param baseFileName file name without extension; a timestamp is appended
 * @param exportFormat 'csv' or 'xlsx'
 * @param sheetName   tab name, xlsx only
 * @returns the generated file name
 */
export const exportRecords = (
  records: Map<string, CellValue>[],
  baseFileName: string,
  exportFormat: ExportFormat,
  sheetName = 'Export'
): string => {
  const table = toTable(records);
  const fileName = `${baseFileName}_${format(new Date(), 'yyyy-MM-dd_HHmm')}.${exportFormat}`;

  if (exportFormat === 'csv') {
    // The BOM is what makes Excel read the file as UTF-8 rather than the system codepage.
    const blob = new Blob(['\uFEFF' + buildCsv(table)], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, fileName);
  } else {
    downloadBlob(buildXlsx(table.headers, table.rows, sheetName), fileName);
  }

  return fileName;
};
